(() => {
  const STORAGE_KEY = 'ow-saved-plants-v1';
  const MAX_ITEMS = 24;
  const dialog = document.getElementById('OwSavedPlantsDialog');
  const list = dialog?.querySelector('[data-ow-saved-list]');
  const empty = dialog?.querySelector('[data-ow-saved-empty]');
  const status = document.querySelector('[data-ow-saved-status]');
  let latest = new Map();
  let refreshController;

  const read = () => {
    try {
      const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      if (!Array.isArray(value)) return [];
      return value.filter((item) => item && item.id && item.url && item.title).slice(0, MAX_ITEMS);
    } catch {
      return [];
    }
  };

  const write = (items) => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items.slice(0, MAX_ITEMS))); } catch {}
  };

  const announce = (message) => {
    if (!status) return;
    status.textContent = '';
    requestAnimationFrame(() => { status.textContent = message; });
  };

  const syncButtons = (items = read()) => {
    const savedIds = new Set(items.map((item) => String(item.id)));
    document.querySelectorAll('[data-ow-save-product]').forEach((button) => {
      const saved = savedIds.has(String(button.dataset.productId));
      const label = saved ? button.dataset.savedLabel : button.dataset.saveLabel;
      button.setAttribute('aria-pressed', saved ? 'true' : 'false');
      button.setAttribute('aria-label', label || 'Save plant');
      const text = button.querySelector('[data-ow-save-label]');
      if (text) text.textContent = label || 'Save plant';
    });
    document.querySelectorAll('[data-ow-saved-count]').forEach((badge) => {
      badge.textContent = String(items.length);
      badge.hidden = items.length === 0;
    });
    document.querySelectorAll('.ow-saved-mobile-trigger').forEach((button) => {
      button.hidden = items.length === 0;
    });
    document.querySelectorAll('[data-ow-saved-only]').forEach((element) => {
      element.hidden = items.length === 0;
    });
  };

  const render = (items = read()) => {
    if (!list || !empty) return;
    list.replaceChildren();
    empty.hidden = items.length > 0;
    items.forEach((item) => {
      const row = document.createElement('li');
      row.className = 'ow-saved-item';
      if (item.image) {
        const image = document.createElement('img');
        image.src = item.image;
        image.alt = '';
        image.width = 76;
        image.height = 76;
        image.loading = 'lazy';
        row.append(image);
      } else {
        const placeholder = document.createElement('span');
        placeholder.className = 'ow-saved-item__placeholder';
        placeholder.setAttribute('aria-hidden', 'true');
        row.append(placeholder);
      }
      const body = document.createElement('div');
      const link = document.createElement('a');
      link.href = item.url;
      link.textContent = item.title;
      const price = document.createElement('p');
      const current = latest.get(String(item.id));
      price.textContent = current?.price || '';
      body.append(link, price);
      const traits = [current?.traitOne, current?.traitTwo].filter(Boolean);
      if (traits.length) {
        const traitList = document.createElement('div');
        traitList.className = 'ow-saved-item__traits';
        traits.forEach((trait) => {
          const chip = document.createElement('span');
          chip.textContent = trait;
          traitList.append(chip);
        });
        body.append(traitList);
      }
      row.append(body);
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.dataset.owSavedRemove = item.id;
      remove.textContent = dialog.dataset.removeLabel || 'Remove';
      remove.setAttribute('aria-label', `${remove.textContent} ${item.title}`);
      row.append(remove);
      list.append(row);
    });
    syncButtons(items);
  };

  const refresh = async () => {
    refreshController?.abort();
    const controller = new AbortController();
    refreshController = controller;
    latest = new Map();
    render();
    const ids = read().map((item) => String(item.id)).filter((id) => /^\d+$/.test(id));
    if (!ids.length) {
      list?.removeAttribute('aria-busy');
      return;
    }
    list?.setAttribute('aria-busy', 'true');
    const timeout = setTimeout(() => controller.abort(), 8000);
    try {
      const url = new URL(`${window.Shopify?.routes?.root || '/'}search`, location.origin);
      url.searchParams.set('section_id', 'ow-saved-products');
      url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'none');
      const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
      if (!response.ok) return;
      const document = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (!document.querySelector('[data-ow-saved-products]')) return;
      const refreshed = new Map();
      document.querySelectorAll('[data-ow-save-product]').forEach((button) => {
        const data = button.dataset;
        if (!ids.includes(data.productId)) return;
        refreshed.set(data.productId, {
          title: data.productTitle, price: data.productPrice || '',
          image: data.productImage || '', traitOne: data.productTraitOne || '', traitTwo: data.productTraitTwo || '',
        });
      });
      if (controller !== refreshController || controller.signal.aborted) return;
      latest = refreshed;
      const items = read().map((item) => ({ ...item, ...(refreshed.get(String(item.id)) || {}) }));
      write(items);
      render(items);
    } catch {
      // Saved links remain usable; old prices and traits are never presented as current.
    } finally {
      clearTimeout(timeout);
      if (controller === refreshController) list?.removeAttribute('aria-busy');
    }
  };

  const open = () => {
    if (!dialog) return;
    render();
    if (!dialog.open) dialog.showModal();
    refresh();
  };

  document.addEventListener('click', async (event) => {
    const target = event.target instanceof Element ? event.target : event.target.parentElement;
    if (!target) return;
    const saveButton = target.closest('[data-ow-save-product]');
    if (saveButton) {
      event.preventDefault();
      event.stopPropagation();
      const id = String(saveButton.dataset.productId || '');
      if (!id) return;
      const items = read();
      const existing = items.findIndex((item) => String(item.id) === id);
      let message;
      if (existing >= 0) {
        items.splice(existing, 1);
        message = `${saveButton.dataset.productTitle || 'Plant'} removed from saved plants.`;
      } else {
        let savedUrl = saveButton.dataset.productUrl || '';
        try {
          const parsedUrl = new URL(savedUrl, window.location.origin);
          ['_pos', '_fid', '_ss'].forEach((parameter) => parsedUrl.searchParams.delete(parameter));
          savedUrl = `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
        } catch {}
        items.unshift({
          id,
          handle: saveButton.dataset.productHandle || '',
          url: savedUrl,
          title: saveButton.dataset.productTitle || 'Plant',
          price: saveButton.dataset.productPrice || '',
          image: saveButton.dataset.productImage || '',
          traitOne: saveButton.dataset.productTraitOne || '',
          traitTwo: saveButton.dataset.productTraitTwo || '',
        });
        message = `${saveButton.dataset.productTitle || 'Plant'} saved.`;
      }
      write(items);
      render(items);
      announce(message);
      return;
    }

    if (target.closest('[data-ow-saved-open]')) { open(); return; }
    if (target.closest('[data-ow-saved-close]')) { dialog?.close(); return; }

    const removeButton = target.closest('[data-ow-saved-remove]');
    if (removeButton) {
      const items = read().filter((item) => String(item.id) !== String(removeButton.dataset.owSavedRemove));
      write(items);
      render(items);
      announce('Plant removed from saved plants.');
      return;
    }

    const shareButton = target.closest('[data-ow-share-product]');
    if (shareButton) {
      const title = shareButton.dataset.shareTitle || document.title;
      const url = window.location.href;
      try {
        if (navigator.share) {
          await navigator.share({ title, url });
        } else {
          await navigator.clipboard.writeText(url);
          announce('Link copied.');
        }
      } catch (error) {
        if (error?.name !== 'AbortError') announce('Use your browser address bar to copy this link.');
      }
    }
  });

  dialog?.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close();
  });
  window.addEventListener('storage', () => render());
  render();
})();
